import React, { Fragment, useEffect, useRef, useState } from "react";
import { Alert, Grid } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { ArrowBack, Crop, CloudUpload, Save } from "@mui/icons-material";
import {
  doc,
  getDoc,
  updateDoc,
  addDoc,
  collection,
  serverTimestamp,
} from "firebase/firestore";
import { format, parse, isValid } from "date-fns";
import { db, auth } from "../../firebase";
import { uploadImage, deleteImageAws } from "../../utils/awsUtils";
import { useUser } from "../context/UserContext";
import { ImageCropModal } from "../common/ImageCropModal";

// Stored dob has no year (e.g. "9 Jun"), but <input type="date"> needs a full
// date — reconstruct one with a placeholder year just so the picker can
// display day/month; the year is discarded again on save.
const dobToInputValue = (dobStr) => {
  if (!dobStr) return "";
  const parsed = parse(`${dobStr} 2000`, "d MMM yyyy", new Date());
  return isValid(parsed) ? format(parsed, "yyyy-MM-dd") : "";
};

const DEPARTMENTS = [
  "Choir",
  "Ushering",
  "Media",
  "Children's Church",
  "Youth",
  "Prayer Team",
  "Welfare",
  "Protocol",
  "Technical",
  "Evangelism",
  "Drama",
  "Member",
  "Other",
];

const UpdateMember = () => {
  const params = useParams();
  const navigate = useNavigate();
  const fileRef = useRef();
  const { userRole, loading: roleLoading } = useUser();

  useEffect(() => {
    if (!roleLoading && userRole === "operator") {
      navigate("/home");
    }
  }, [roleLoading, userRole, navigate]);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [gender, setGender] = useState("");
  const [dob, setDob] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [department, setDepartment] = useState([]);
  const [departmentAdditional, setDepartmentAdditional] = useState("");
  const [favHymns, setFavHymns] = useState("");
  const [favBibleVerse, setFavBibleVerse] = useState("");
  const [currentImgSrc, setCurrentImgSrc] = useState("");
  const [currentImgId, setCurrentImgId] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [rawImageSrc, setRawImageSrc] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    const fetchMember = async () => {
      try {
        const snap = await getDoc(doc(db, "members", params.id));
        if (snap.exists()) {
          const d = snap.data();
          if (d.firstName || d.lastName) {
            setFirstName(d.firstName || "");
            setLastName(d.lastName || "");
          } else {
            // Legacy record from before firstName/lastName were split out.
            const [first, ...rest] = (d.name || "").trim().split(" ");
            setFirstName(first || "");
            setLastName(rest.join(" "));
          }
          setGender(d.gender || "");
          setDob(dobToInputValue(d.dob || ""));
          setPhone(d.phone || "");
          setAddress(d.address || "");
          setDepartment(
            Array.isArray(d.department)
              ? d.department
              : typeof d.department === "string" && d.department
                ? d.department.split(", ").filter(Boolean)
                : [],
          );
          const rawHymn = d.favHymns || "";
          setFavHymns(
            rawHymn.startsWith("CCC Hymn ")
              ? rawHymn.replace("CCC Hymn ", "")
              : rawHymn,
          );
          setFavBibleVerse(d.favBibleVerse || "");
          setCurrentImgSrc(d.imgSrc || "");
          setCurrentImgId(d.imgId || "");
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchMember();
  }, [params.id]);

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setRawImageSrc(URL.createObjectURL(file));
    setCropModalOpen(true);
    setErrorMsg("");
  };

  const handleCropped = (blob) => {
    setCropModalOpen(false);
    if (blob.size > 2 * 1024 * 1024) {
      setErrorMsg("Cropped image must be under 2MB");
      return;
    }
    setImageFile(new File([blob], "profile.jpg", { type: blob.type || "image/jpeg" }));
    setImagePreview(URL.createObjectURL(blob));
    setErrorMsg("");
  };

  // Re-crop the member's existing photo (not a new upload) — the result is
  // staged the same way a freshly-selected file would be, so it only takes
  // effect (uploads + replaces the old file) once Save Changes is clicked.
  const handleCropExisting = () => {
    const src = imagePreview || currentImgSrc;
    if (!src) return;
    setRawImageSrc(src);
    setCropModalOpen(true);
  };

  const handleUpdate = async () => {
    if (!firstName.trim()) return setErrorMsg("First name cannot be empty");
    if (!lastName.trim()) return setErrorMsg("Last name cannot be empty");
    if (!gender) return setErrorMsg("Please select a gender");
    if (!phone) return setErrorMsg("Phone cannot be empty");
    const trimmedAdditional = departmentAdditional.trim();
    if (!department.length && !trimmedAdditional)
      return setErrorMsg("Please select at least one department");

    setLoading(true);
    try {
      let imgSrc = currentImgSrc;
      let imgId = currentImgId;

      if (imageFile) {
        const uploaded = await uploadImage(imageFile);
        if (currentImgId) await deleteImageAws(currentImgId);
        imgSrc = uploaded.imgUrl;
        imgId = uploaded.imgId;
      }

      const trimmedFirstName = firstName.trim();
      const trimmedLastName = lastName.trim();
      const fullName = `${trimmedFirstName} ${trimmedLastName}`.trim();
      const finalDepartments = trimmedAdditional
        ? [trimmedAdditional, ...department.filter((d) => d !== trimmedAdditional)]
        : department;

      await updateDoc(doc(db, "members", params.id), {
        firstName: trimmedFirstName,
        lastName: trimmedLastName,
        lastNameLower: trimmedLastName.toLowerCase(),
        name: fullName,
        gender,
        dob: dob ? format(new Date(dob + "T00:00:00"), "d MMM") : "",
        phone,
        address: address || "",
        department: finalDepartments.join(", "),
        favHymns: favHymns ? `CCC Hymn ${favHymns}` : "",
        favBibleVerse,
        imgSrc,
        imgId,
      });

      const userName = auth.currentUser?.email || "Admin";
      await addDoc(collection(db, "notifications"), {
        name: userName,
        message: `Updated Member: ${fullName}`,
        createdAt: serverTimestamp(),
      });

      toast("Member updated successfully!", { type: "success" });
      setTimeout(() => navigate("/members"), 2000);
    } catch (err) {
      setErrorMsg(err.message || "Failed to update member");
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full px-4 py-3 border border-gray-200 rounded-xl font-abel text-sm text-gray-700 focus:outline-none focus:border-[#000C66] focus:ring-2 focus:ring-[#000C66]/10 transition bg-white";
  const labelClass =
    "block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 font-abel";

  return (
    <Fragment>
      <div className="min-h-screen bg-[#f5f7fe] p-3 sm:p-6">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <button
              onClick={() => navigate("/members")}
              className="p-2 rounded-xl hover:bg-white border border-transparent hover:border-gray-100 transition"
            >
              <ArrowBack className="text-gray-400" style={{ fontSize: 20 }} />
            </button>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-[#000C66] font-aref">
                Update Member
              </h1>
              <p className="text-gray-400 text-xs font-abel mt-0.5">
                Edit member details
              </p>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-8">
            {errorMsg && (
              <Alert
                severity="error"
                className="mb-5"
                onClose={() => setErrorMsg("")}
              >
                {errorMsg}
              </Alert>
            )}

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <label className={labelClass}>First Name *</label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => {
                    setFirstName(e.target.value);
                    setErrorMsg("");
                  }}
                  className={inputClass}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <label className={labelClass}>Last Name *</label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => {
                    setLastName(e.target.value);
                    setErrorMsg("");
                  }}
                  className={inputClass}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <label className={labelClass}>Gender *</label>
                <select
                  value={gender}
                  onChange={(e) => {
                    setGender(e.target.value);
                    setErrorMsg("");
                  }}
                  className={inputClass}
                >
                  <option value="">-- Select Gender --</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </Grid>

              <Grid item xs={12} sm={6}>
                <label className={labelClass}>Date of Birth</label>
                <input
                  type="date"
                  value={dob}
                  onChange={(e) => {
                    setDob(e.target.value);
                    setErrorMsg("");
                  }}
                  className={inputClass}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <label className={labelClass}>Phone *</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => {
                    setPhone(e.target.value);
                    setErrorMsg("");
                  }}
                  className={inputClass}
                />
              </Grid>

              <Grid item xs={12}>
                <label className={labelClass}>Address</label>
                <input
                  type="text"
                  value={address}
                  onChange={(e) => {
                    setAddress(e.target.value);
                    setErrorMsg("");
                  }}
                  placeholder="e.g. 12 Church Street, Lagos"
                  className={inputClass}
                />
              </Grid>

              <Grid item xs={12}>
                <label className={labelClass}>
                  Department *{" "}
                  <span className="text-gray-300 normal-case">
                    (select all that apply)
                  </span>
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-1">
                  {DEPARTMENTS.map((d) => (
                    <label
                      key={d}
                      className={`flex items-center gap-2 px-3 py-2 rounded-xl border cursor-pointer transition text-sm font-abel select-none ${
                        department.includes(d)
                          ? "border-[#000C66] bg-[#000C66]/5 text-[#000C66] font-semibold"
                          : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="hidden"
                        checked={department.includes(d)}
                        onChange={() => {
                          setDepartment((prev) =>
                            prev.includes(d)
                              ? prev.filter((x) => x !== d)
                              : [...prev, d],
                          );
                          setErrorMsg("");
                        }}
                      />
                      <span
                        className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition ${
                          department.includes(d)
                            ? "bg-[#000C66] border-[#000C66]"
                            : "border-gray-300"
                        }`}
                      >
                        {department.includes(d) && (
                          <svg
                            className="w-2.5 h-2.5 text-white"
                            fill="none"
                            viewBox="0 0 10 10"
                          >
                            <path
                              d="M1.5 5l2.5 2.5 4.5-4.5"
                              stroke="currentColor"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </span>
                      {d}
                    </label>
                  ))}
                </div>
              </Grid>

              {(userRole === "admin" || userRole === "superadmin") && (
                <Grid item xs={12}>
                  <label className={labelClass}>
                    Department Additional{" "}
                    <span className="text-gray-300 normal-case">
                      (optional role, added to the front of Department on save)
                    </span>
                  </label>
                  <input
                    type="text"
                    value={departmentAdditional}
                    onChange={(e) => {
                      setDepartmentAdditional(e.target.value);
                      setErrorMsg("");
                    }}
                    placeholder="e.g. PRO, Choir Director…"
                    className={inputClass}
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <label className={labelClass}>Favourite Hymn</label>
                <div className="flex items-center border border-gray-200 rounded-xl overflow-hidden focus-within:border-[#000C66] focus-within:ring-2 focus-within:ring-[#000C66]/10 transition bg-white">
                  <span className="px-3 py-3 bg-gray-50 border-r border-gray-200 text-gray-500 text-sm font-abel whitespace-nowrap select-none">
                    CCC Hymn
                  </span>
                  <input
                    type="number"
                    min="1"
                    value={favHymns}
                    onChange={(e) => {
                      setFavHymns(e.target.value);
                      setErrorMsg("");
                    }}
                    placeholder="767"
                    className="flex-1 px-3 py-3 text-sm font-abel text-gray-700 bg-white focus:outline-none"
                  />
                </div>
              </Grid>

              <Grid item xs={12}>
                <label className={labelClass}>Favourite Bible Verse</label>
                <input
                  type="text"
                  value={favBibleVerse}
                  onChange={(e) => {
                    setFavBibleVerse(e.target.value);
                    setErrorMsg("");
                  }}
                  placeholder="e.g., John 3:16"
                  className={inputClass}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <label className={labelClass}>
                  Profile Image{" "}
                  <span className="text-gray-300 normal-case">
                    (leave empty to keep current)
                  </span>
                </label>
                {(imagePreview || currentImgSrc) && (
                  <div className="relative inline-block mb-2">
                    <img
                      src={imagePreview || currentImgSrc}
                      alt="preview"
                      className="h-24 w-24 object-cover rounded-xl border border-gray-100"
                    />
                    <button
                      type="button"
                      onClick={handleCropExisting}
                      title="Crop this photo"
                      className="absolute -bottom-2 -right-2 w-7 h-7 rounded-full bg-[#000C66] text-white flex items-center justify-center shadow-sm hover:bg-blue-900 transition"
                    >
                      <Crop style={{ fontSize: 14 }} />
                    </button>
                  </div>
                )}
                <div
                  className="border-2 border-dashed border-gray-200 rounded-xl p-4 text-center cursor-pointer hover:border-[#000C66] transition"
                  onClick={() => fileRef.current.click()}
                >
                  <CloudUpload
                    className="text-gray-300"
                    style={{ fontSize: 24 }}
                  />
                  <p className="text-xs text-gray-400 font-abel mt-1">
                    {imageFile ? imageFile.name : "Click to replace photo"}
                  </p>
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageSelect}
                />
              </Grid>

              <Grid item xs={12} className="flex justify-end">
                <button
                  onClick={handleUpdate}
                  disabled={loading}
                  className="w-full sm:w-auto flex items-center justify-center gap-2 bg-[#000C66] text-white px-8 py-3 rounded-xl font-abel font-semibold hover:bg-blue-900 transition disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <Save style={{ fontSize: 18 }} />
                  )}
                  {loading ? "Saving..." : "Save Changes"}
                </button>
              </Grid>
            </Grid>
          </div>
        </div>
      </div>

      <ImageCropModal
        open={cropModalOpen}
        imageSrc={rawImageSrc}
        aspect={1}
        onCancel={() => setCropModalOpen(false)}
        onCropped={handleCropped}
      />
    </Fragment>
  );
};

export default UpdateMember;
